/*
 * Copyright (c) 2022. Muzology
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { RudderstackProvider, useRudderStackAnalytics } from './RudderstackProvider';
import { BUILD_ENVIRONMENT, CODE_BRANCH, CODE_VERSION, GOOGLE_TAG_MANAGER_ID, HEAP_ANALYTICS, LOGROCKET_ID, MUZOLOGY_API, ROLLBAR_TOKEN, WEBSITE_URL } from '@/lib/defaults';
import LogRocket from 'logrocket';
import { gaEvent } from '@/lib/ga';
import { Provider as RollbarProvider, useRollbar } from '@rollbar/react';

const Context = React.createContext(undefined);

/* eslint-disable camelcase */
const rollbarConfig = {
    accessToken: ROLLBAR_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    payload: {
        environment: BUILD_ENVIRONMENT,
        //trace_id: 'abc',
        client: {
            javascript: {
                code_version: CODE_VERSION,
                source_map_enabled: true,
                guess_uncaught_frames: true
            }
        },
        server: {
            root: '/',
            host: WEBSITE_URL,
            branch: CODE_BRANCH
        }
    }
};
/* eslint-enable camelcase */

export const AnalyticsProvider = ({ children, pageProps }) => {
    const [logRocketSessionURL, setLogRocketSessionURL] = React.useState(null);
    const value = { logRocketSessionURL };

    React.useEffect(() => {
        if (GOOGLE_TAG_MANAGER_ID) {
            console.info('[Analytics] Google Tag Manager ID:', GOOGLE_TAG_MANAGER_ID);
        } else {
            console.warn('[Analytics] No Google Tag Manager ID');
        }

        if (ROLLBAR_TOKEN) {
            console.info('[Analytics] Rollbar Token:', ROLLBAR_TOKEN);
        } else {
            console.warn('[Analytics] No Rollbar Token');
        }

        if (HEAP_ANALYTICS) {
            console.info('[Analytics] Heap Analytics:', HEAP_ANALYTICS);
        } else {
            console.warn('[Analytics] No Heap Analytics');
        }

        if (LOGROCKET_ID) {
            console.info('[Analytics] LogRocket ID', LOGROCKET_ID);
        } else {
            console.warn('No LogRocket ID');
        }
    }, []);

    return (
        <RollbarProvider config={rollbarConfig}>
            <Context.Provider value={value}>
                <RudderstackProvider pageProps={pageProps}>
                    <AnalyticsIdentity setLogRocketSessionURL={setLogRocketSessionURL}>
                        {children}
                    </AnalyticsIdentity>
                    {/*
                    */}
                </RudderstackProvider>
            </Context.Provider>
        </RollbarProvider>
    );
};


const AnalyticsIdentity = ({ children, setLogRocketSessionURL }) => {
    const analytics = useAnalytics();
    const rudderstack = useRudderStackAnalytics();
    const rollbar = useRollbar();

    // rollbar?.info('Rollbar installed');

    const getHeap = () => window.heap;

    useEffect(() => {
        const heap = getHeap();
        console.log('[heap] push', heap?.push);
        console.log('[heap] heap', heap);
    }, []);

    useEffect(() => {
        console.log('[Analytics Identity] initializing analytics identity', { rudderstack, analytics });
        if (!rudderstack) {
            console.warn('[Analytics Identity] Rudderstack not available');
            return;
        }

        // identify with LogRocket on the client side - only in production mode
        try {
            const anonymousId = analytics?.getAnonymousId();
            console.log('[Analytics] anonymousId = ', anonymousId);
            if (LOGROCKET_ID) {
                console.log('[Logrocket] init ', LOGROCKET_ID);
                LogRocket.init(LOGROCKET_ID, { rootHostname: 'muzology.com' });
                console.log('[Logrocket] identify ', LOGROCKET_ID);
                LogRocket.identify({ anonymousId });

                const heap = getHeap();
                console.log('[heap] mount heap =', heap);
                // console.log('[heap] mount heap.push =', heap?.push);
                // console.log('[heap] mount heap.addUserProps = ', heap?.addUserProperties);
                if (window.heap) {
                    LogRocket.getSessionURL((logRocketSessionUrl: string) => {
                        window.heap.track('LogRocket Session URL', { logRocketSessionUrl });
                    });
                    /*
                        const timestamp = new Date().toISOString();
                        LogRocket.getSessionURL((logRocketSessionUrl: string) => {
                            console.log('[heap] add user properties', { logRocketSessionUrl });
                            const key = `logRocketSessionURL-${timestamp}`;
                            try {
                                const payload = {
                                    logRocketSessionUrl, anonymousId
                                };
                                rollbar?.info('Posting identity', payload);
                                console.log('[heap] heap =', heap);
                                console.log('[heap] addUserProps = ', heap.addUserProperties);
                                if (heap.push) {
                                    heap?.addUserProperties(payload);
                                } else {
                                    console.error('[heap] heap.push not available');
                                    rollbar?.error('heap.push not available');
                                }
                            } catch (e) {
                                console.error('[heap] error saving heap identity', e);
                                rollbar?.error('error saving heap identity', e);
                            }

                            // the user property will get overwritten each session,
                            // so add an event to tag the session with the exact url for this session
                            analytics.track('LogRocket Session URL', {
                                logRocketSessionUrl, timestamp
                            });
                            // save the session url for later use
                            if (setLogRocketSessionURL) setLogRocketSessionURL(logRocketSessionUrl);
                        });
                     */
                } else {
                    console.warn('[Heap] Heap not available');
                }
            } else {
                console.warn('[Logrocket] LogRocket not available');
            }
        } catch (e) {
            console.error('[Logrocket] error setting up analytics', e);
            rollbar?.error('error setting up analytics', e);
        }
    }, [rudderstack]);

    return children;
};

export const useAnalytics = () => {
    const rollbar = useRollbar();
    const getHeap = () => window.heap;
    const rudderstack = useRudderStackAnalytics();
    // const context = useContext(Context);
    // if (!context) {
    //     throw new Error('useAnalytics must be used within an AnalyticsProvider');
    // }
    return useMemo(() => (
        {
            rudderstack: {},
            anonymousId: rudderstack?.getAnonymousId(),
            track(...args) {
                console.debug('[Analytics] Track:', ...args);
                // try {
                //     if (args.length === 1) {
                //         gaEvent(args[0]);
                //     } else if (args.length > 1) {
                //         gaEvent(args[0], args[1]);
                //     }
                // } catch (e) {
                //     console.error('[GA] error tracking event', e);
                //     rollbar?.error('error tracking event', e);
                // }

                try {
                    const heap = getHeap();
                    if (heap) {
                        heap.track(...args);
                    }
                } catch (e) {
                    console.error('[Heap] error tracking event', e);
                    rollbar?.error('error tracking event', e);
                }
                if (rudderstack) {
                    return rudderstack.track(...args);
                }
                // return a promise to keep the API consistent
                console.warn('[Analytics] Rudderstack not available');
                return Promise.resolve();
            },
            page(...args) {
                console.debug('[Analytics] Page:', ...args);
                // if (rudderstack) {
                //     rudderstack.page(...args);
                // }
            },
            identify(...args) {
                console.debug('[Analytics] Identify:', ...args);
                if (rudderstack)
                    rudderstack.identify(...args);
                const heap = getHeap();
                // console.log('[heap] heap =', heap);
                // console.log('[heap] heap.push =', heap?.push);
                // console.log('[heap] heap.addUserProps = ', heap.addUserProperties);
                try {
                    if (heap) {
                        // heap.identify(anonymousId);
                        heap.addUserProperties(args as any);
                    }
                } catch (e) {
                    console.error('[Heap] error identifying user', e);
                    rollbar?.error('error identifying user', e);
                }

                try {
                    if (LOGROCKET_ID) {
                        LogRocket.identify(args as any);
                    }
                } catch (e) {
                    console.error('[LogRocket] error identifying user', e);
                    rollbar?.error('error identifying user', e);
                }

                /*
                if (heap) {
                    try {
                        rollbar?.info('Identifying user', args);
                        console.log('[heap] heap =', heap);
                        console.log('[heap] addUserProps = ', heap.addUserProperties);
                        if (heap.push) {
                            heap.addUserProperties(args as any);
                        } else {
                            console.error('[heap] heap.push not available');
                            rollbar?.error('heap.push not available');
                        }

                    } catch (e) {
                        console.error('[Heap] error identifying user', e);
                        rollbar?.error('error identifying user', e);
                    }
                }
                */

            },
            getAnonymousId() {
                const id = rudderstack?.getAnonymousId() ?? null;
                console.log('[Analytics] getAnonymousId()', id);
                return id;
            },
            getLogrocketSessionURL() {
                console.log('[Analytics] getLogrocketSessionURL()');
                // return context.logRocketSessionURL;
                return "logrocket url";
            },
            reset() {
                console.log('[Analytics] reset()');
                // if (rudderstack) {
                //     rudderstack.reset();
                // }
            }
        }
    ), [rollbar, rudderstack]);
};

export const BlockKlaviyo = { integrations: { All: true, Klaviyo: false } };

export default AnalyticsProvider;
